import { useEffect, useState } from 'react';
import { Card, Typography, Button, Tooltip, theme, Tag, EmptyState, Modal, message, Tree, Form, Input, Select, Dropdown, MenuItem, MenuLabel, MenuDivider, type TagTone } from '../../ui';
import { Plus, Pencil, Trash2, Download, Upload as UploadIcon, ArrowUpDown, FileSpreadsheet, FileDown } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { BulkImportModal } from '../../components/BulkImportModal';
import { TemplateButton } from '../../components/TemplateButton';
import { ExportDialog } from '../../components/ExportDialog';
import { downloadCsv } from '../../lib/downloadCsv';

type Level = 'zone' | 'state' | 'district' | 'beat';
// The one hierarchy rule, mirrored from the backend: each level's child + parent level.
const CHILD_OF: Record<Level, Level | null> = { zone: 'state', state: 'district', district: 'beat', beat: null };
const PARENT_OF: Record<Level, Level | null> = { zone: null, state: 'zone', district: 'state', beat: 'district' };
const ALL_LEVELS: Level[] = ['zone', 'state', 'district', 'beat'];
const LEVEL_META: Record<Level, { label: string; color: TagTone }> = {
  zone: { label: 'Zone', color: 'purple' },
  state: { label: 'State', color: 'blue' },
  district: { label: 'District', color: 'cyan' },
  beat: { label: 'Beat', color: 'green' },
};

const TREE_KEYS = ['territories-tree', 'territories-list', 'territories-flat'];

// Depth-first flatten of the tree → every node, for the parent picker.
function flatten(nodes: any[], acc: any[] = []): any[] {
  for (const n of nodes) { acc.push(n); if (n.children?.length) flatten(n.children, acc); }
  return acc;
}

interface FormState {
  mode: 'create' | 'edit';
  level: Level;
  parent?: any | null; // parent node (create)
  record?: any;        // node being edited (edit)
}

export function TerritoriesPage({ embedded }: { embedded?: boolean } = {}) {
  const { token } = theme.useToken();
  const [formState, setFormState] = useState<FormState | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const canManage = usePermission('TERRITORY.ADMIN'); // add/edit/delete/import needs ADMIN; VIEW is read-only
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['territories-tree'],
    queryFn: () => request<any[]>({ url: '/territories/tree' }),
  });
  const flat = flatten(data ?? []);

  const del = useMutation({
    mutationFn: (id: string) => request({ url: `/territories/${id}`, method: 'DELETE' }),
    onSuccess: () => { message.success('Territory deleted'); TREE_KEYS.forEach((k) => qc.invalidateQueries({ queryKey: [k] })); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to delete'),
  });

  // Check usage first so we show WHY it can't be deleted (rather than a failed request), then confirm.
  const onDelete = async (t: any) => {
    try {
      const usage: any = await request({ url: `/territories/${t.id}/usage` });
      if (!usage?.deletable) {
        const parts = Object.entries(usage?.counts ?? {}).filter(([, n]: any) => n > 0).map(([k, n]: any) => `${n} ${k}`);
        Modal.error({
          title: `Can't delete ${t.code} · ${t.name}`,
          content: `Still linked to ${parts.join(', ') || 'other records'}. Reassign or merge them into another territory first, then delete.`,
        });
        return;
      }
      Modal.confirm({
        title: `Delete ${t.code} · ${t.name}?`,
        content: 'Nothing is assigned to it, so this is safe. It will be removed from the territory list, and this cannot be undone.',
        okText: 'Delete', okButtonProps: { danger: true },
        onOk: () => del.mutateAsync(String(t.id)),
      });
    } catch {
      message.error('Could not check what uses this territory.');
    }
  };

  // Export the whole structure as CSV (round-trips the Territories import format).
  const onExport = () => downloadCsv('/territories/export.csv', 'territories.csv').catch(() => message.error('Export failed'));
  // Two exports, deliberately: the formatted master people read, and the CSV that round-trips through
  // the bulk importer (its headers match the template).
  const [exportOpen, setExportOpen] = useState(false);

  function convert(t: any): any {
    const childCount = t.children?.length ?? 0;
    const meta = LEVEL_META[t.level as Level];
    const childLevel = CHILD_OF[t.level as Level];
    return {
      key: String(t.id),
      title: (
        <div className="gb-tnode">
          <span className="gb-tnode-main">
            <Tag tone={meta?.color} className="gb-tnode-pill">{meta?.label ?? t.level}</Tag>
            <b style={{ fontVariantNumeric: 'tabular-nums' }}>{t.code}</b>
            <span className="gb-tnode-name">{t.name}</span>
            {childCount > 0 && <span className="gb-tnode-count">{childCount}</span>}
            {t.stateCode && <span className="gb-tnode-gst">GST {t.stateCode}</span>}
          </span>
          {canManage && (
            <span className="gb-tnode-actions">
              {childLevel && (
                <Tooltip label={`Add ${LEVEL_META[childLevel].label}`}>
                  <Button variant="text" size="sm" aria-label={`Add ${LEVEL_META[childLevel].label}`} icon={<Plus size={16} />}
                    onClick={(e) => { e.stopPropagation(); setFormState({ mode: 'create', level: childLevel, parent: t }); }} />
                </Tooltip>
              )}
              <Tooltip label="Edit">
                <Button variant="text" size="sm" aria-label="Edit" icon={<Pencil size={16} />}
                  onClick={(e) => { e.stopPropagation(); setFormState({ mode: 'edit', level: t.level, record: t }); }} />
              </Tooltip>
              <Tooltip label="Delete">
                <Button variant="soft-danger" size="sm" aria-label="Delete" icon={<Trash2 size={16} />}
                  onClick={(e) => { e.stopPropagation(); onDelete(t); }} />
              </Tooltip>
            </span>
          )}
        </div>
      ),
      children: (t.children ?? []).map(convert),
    };
  }
  const treeData = (data ?? []).map(convert);

  return (
    <div>
      {!embedded && <Typography.Title level={4} style={{ marginBottom: 2 }}>Territories</Typography.Title>}
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Hierarchy: Zone → State → District → Beat. Click <Plus size={14} style={{ display: 'inline', verticalAlign: '-2px' }} /> on a row to add the next level beneath it.
      </Typography.Paragraph>

      <Card>
        {/*
          * One primary action left, one file hub right — the same consolidation the retailer and
          * product registers use.
          *
          * Four buttons in three different weights used to sit in a row here, and three of them
          * named a FILE rather than an action: nothing told you what separated "Export" from "CSV
          * for re-import" (one is the formatted master people read, the other round-trips through
          * the importer), and "CSV for re-import" was a borderless text button with no icon wedged
          * between bordered ones, so it read as a stray link. Grouped by intent, each line says
          * what it is FOR and the format is a trailing detail.
          */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {canManage && (
            <Button variant="primary" size="sm" icon={<Plus size={16} />}
              onClick={() => setFormState({ mode: 'create', level: 'zone', parent: null })}>
              Add Zone
            </Button>
          )}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Dropdown
              align="end"
              panel={
                <>
                  <MenuLabel>Download</MenuLabel>
                  <MenuItem icon={<Download size={16} />} onClick={() => setExportOpen(true)}>
                    Territory master — PDF or Excel
                  </MenuItem>
                  <MenuItem icon={<FileSpreadsheet size={16} />} onClick={onExport}>
                    Territory data — CSV
                  </MenuItem>
                  {canManage && <MenuDivider />}
                  {canManage && <MenuLabel>Bulk import</MenuLabel>}
                  {canManage && (
                    <MenuItem icon={<UploadIcon size={16} />} onClick={() => setImportOpen(true)}>
                      Import from CSV…
                    </MenuItem>
                  )}
                  {canManage && (
                    <MenuItem
                      icon={<FileDown size={16} />}
                      onClick={() => downloadCsv('/imports/templates/territories.csv', 'territories-template.csv')
                        .catch(() => message.error('Template unavailable'))}
                    >
                      Blank template — CSV
                    </MenuItem>
                  )}
                </>
              }
            >
              {/* Two-way hub, so a lone download arrow would announce only half of it. */}
              <Button size="sm" icon={<ArrowUpDown size={16} />}>Import / export</Button>
            </Dropdown>
          </div>
        </div>
        {treeData.length === 0 && !isLoading ? (
          <EmptyState title="No territories yet — add the root Zone to begin." />
        ) : (
          <Tree
            className="gb-territory-tree"
            defaultExpandDepth={1}
            showLine={{ showLeafIcon: false }}
            blockNode
            selectable={false}
            treeData={treeData}
          />
        )}
      </Card>

      <TerritoryFormModal state={formState} allTerritories={flat} onClose={() => setFormState(null)} />
      <BulkImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => TREE_KEYS.forEach((k) => qc.invalidateQueries({ queryKey: [k] }))}
        kind="territories"
        entityPlural="territories"
        columns="code, name, level, parentCode, stateCode"
        note={<>Parents are referenced by <b>code</b>; row order doesn’t matter. </>}
      />

      <style>{`
        .gb-territory-tree .ant-tree-treenode { padding: 4px 0; align-items: center; }
        .gb-territory-tree .ant-tree-node-content-wrapper { width: 100%; transition: background .15s ease; }
        .gb-territory-tree .ant-tree-node-content-wrapper:hover { background: ${token.colorFillTertiary}; }
        .gb-tnode { display: flex; align-items: center; gap: 6px; padding-right: 8px; }
        /* Wraps: on a phone the tree indent leaves ~200px for the label, and the code + name + child
           count + "GST 27" chip ran straight off the row and were clipped away by the tree (measured:
           "GST 23" lost 72px at 320, "Madhya Pradesh" 24px), with no tooltip to recover them. */
        .gb-tnode-main { display: inline-flex; flex-wrap: wrap; align-items: center; gap: 4px 8px; min-width: 0; }
        .gb-tnode-pill { margin: 0; font-size: 10px; line-height: 16px; text-transform: uppercase; letter-spacing: .4px; border: none; }
        .gb-tnode-name { color: ${token.colorTextSecondary}; }
        .gb-tnode-count { font-size: 11px; color: ${token.colorTextTertiary}; background: ${token.colorFillSecondary}; border-radius: 10px; padding: 0 8px; line-height: 18px; }
        .gb-tnode-gst { font-size: 10px; color: ${token.colorTextTertiary}; border: 1px solid ${token.colorBorderSecondary}; border-radius: 4px; padding: 0 6px; line-height: 16px; }
        /* Always visible (no hover needed), sitting just after the row label. */
        .gb-tnode-actions { display: inline-flex; gap: 2px; opacity: .55; transition: opacity .15s ease; flex-shrink: 0; }
        .gb-territory-tree .ant-tree-treenode:hover .gb-tnode-actions { opacity: 1; }
      `}</style>
    {/* No period picker: the territory master is a structural snapshot, not a date-ranged log. */}
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Export territory master"
        description="The tree flattened — code, name, level and parent — so the hierarchy survives in a spreadsheet."
        baseUrl="/territories/export"
        hidePeriod
      />
    </div>
  );
}

function TerritoryFormModal({ state, allTerritories, onClose }: { state: FormState | null; allTerritories: any[]; onClose: () => void }) {
  const [form] = Form.useForm();
  const { token } = theme.useToken();
  const qc = useQueryClient();

  const open = !!state;
  const isEdit = state?.mode === 'edit';
  const record = state?.record;
  const parent = state?.parent ?? null;
  const createLevel: Level = state?.level ?? 'zone';
  const hasChildren = (record?.children?.length ?? 0) > 0;

  // On edit the level is reactive (its parent picker + GST field follow it); on create it's fixed by
  // where the user clicked. Kept in state so the dependent fields re-render as the level changes.
  const [selLevel, setSelLevel] = useState<Level>('zone');

  // Seed the form from `record` via initialValues + a per-target key, NOT via setFieldsValue in an
  // effect. The ui Form seeds its store from initialValues on mount; the effect path raced the Radix
  // modal's mount — leaving the edit form blank (values set before the fields registered) and letting
  // a previous edit's values leak in (the stale "IN-PB" → duplicate-key error). The key remounts the
  // form fresh for each target, so it always reflects exactly the territory being edited.
  const editValues =
    isEdit && record
      ? {
          code: record.code,
          name: record.name,
          stateCode: record.stateCode ?? undefined,
          level: record.level,
          parentId: record.parentId ? String(record.parentId) : undefined,
        }
      : {};
  const formKey = isEdit ? `edit-${record?.id}` : `create-${createLevel}-${parent?.id ?? 'root'}`;

  // Keep the reactive level (drives the parent picker + GST visibility) in sync with the open target.
  useEffect(() => {
    if (!open) return;
    setSelLevel(isEdit && record ? record.level : createLevel);
  }, [open, isEdit, record, createLevel]);

  const meta = LEVEL_META[isEdit ? selLevel : createLevel];
  const showGst = selLevel === 'state' || selLevel === 'district';
  const parentLevel = PARENT_OF[selLevel];
  const parentOptions = allTerritories
    .filter((t) => t.level === parentLevel && String(t.id) !== String(record?.id))
    .map((t) => ({ value: String(t.id), label: `${t.code} · ${t.name}` }));

  const save = useConfirmedMutation({ title: "Save this territory?", content: "Coverage and customer routing follow the territory structure.", okText: "Yes, save" }, {
    mutationFn: (vals: any) =>
      isEdit
        ? request({
            url: `/territories/${record.id}`, method: 'PUT',
            data: {
              code: vals.code?.trim(), name: vals.name?.trim(),
              stateCode: showGst ? (vals.stateCode || null) : null,
              level: selLevel,
              parentId: selLevel === 'zone' ? null : (vals.parentId ? String(vals.parentId) : null),
            },
          })
        : request({
            url: '/territories', method: 'POST',
            data: {
              code: vals.code?.trim(), name: vals.name?.trim(), stateCode: vals.stateCode || null,
              level: createLevel, parentId: parent ? String(parent.id) : undefined,
            },
          }),
    onSuccess: () => {
      message.success(isEdit ? 'Territory updated' : `${meta.label} created`);
      TREE_KEYS.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to save'),
  });

  const editLevelLabel = LEVEL_META[record?.level as Level]?.label ?? 'Territory';

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={isEdit ? `Edit ${editLevelLabel}` : `New ${meta.label}`}
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>{isEdit ? 'Save' : `Create ${meta.label}`}</Button>
      </>}
    >
      <div style={{
        marginBottom: 18, padding: '10px 12px', borderRadius: 8,
        background: token.colorFillQuaternary, border: `1px solid ${token.colorBorderSecondary}`, fontSize: 13,
      }}>
        {isEdit ? (
          <span>
            Editing <Tag tone={LEVEL_META[record?.level as Level]?.color} style={{ margin: 0 }}>{editLevelLabel}</Tag> <b>{record?.code}</b>
            {hasChildren && <span style={{ color: token.colorTextTertiary }}> · has sub-territories, so its level is locked</span>}
          </span>
        ) : parent ? (
          <span>
            New <Tag tone={meta.color} style={{ margin: 0 }}>{meta.label}</Tag> under{' '}
            <Tag tone={LEVEL_META[parent.level as Level]?.color} style={{ margin: 0 }}>{LEVEL_META[parent.level as Level]?.label}</Tag>{' '}
            <b>{parent.code}</b> · {parent.name}
          </span>
        ) : (
          <span>New root <Tag tone={meta.color} style={{ margin: 0 }}>Zone</Tag> — the top of the territory hierarchy.</span>
        )}
      </div>

      <Form key={formKey} form={form} layout="vertical" initialValues={editValues} onFinish={(v) => save.mutate(v)}>
        <Form.Item name="code" label="Code" rules={[{ required: true, message: 'A unique code is required' }]}
          extra="Short unique identifier — e.g. KA-MYS">
          <Input placeholder={parent ? `${parent.code}-XXX` : 'IN'} autoFocus={!isEdit} allowClear />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
          <Input placeholder={`${meta.label} name`} allowClear />
        </Form.Item>

        {/* Level + parent are only editable on an EXISTING territory; on create they're fixed by context. */}
        {isEdit && (
          <>
            <Form.Item name="level" label="Level"
              extra={hasChildren ? 'Locked — move or merge its sub-territories to change the level.' : 'Changing the level re-homes it; pick the new parent below.'}>
              <Select
                disabled={hasChildren}
                onChange={(v: Level) => { setSelLevel(v); form.setFieldsValue({ parentId: undefined }); }}
                optionLabelProp="label"
                options={ALL_LEVELS.map((l) => ({
                  value: l,
                  // Colored pill matching the tree (Zone=purple · State=blue · District=cyan · Beat=green).
                  label: (
                    <Tag tone={LEVEL_META[l].color}
                      style={{ margin: 0, border: 'none', textTransform: 'uppercase', fontSize: 10, letterSpacing: '.4px', lineHeight: '18px' }}>
                      {LEVEL_META[l].label}
                    </Tag>
                  ),
                }))}
              />
            </Form.Item>
            {selLevel !== 'zone' && (
              <Form.Item name="parentId" label={`Parent (${LEVEL_META[parentLevel as Level]?.label ?? ''})`}
                rules={[{ required: true, message: `Pick a ${parentLevel} to sit under` }]}>
                <Select
                  showSearch optionFilterProp="label"
                  placeholder={`Select a ${parentLevel}`}
                  options={parentOptions}
                  notFoundContent={`No ${parentLevel}-level territories exist`}
                />
              </Form.Item>
            )}
          </>
        )}

        {showGst && (
          <Form.Item name="stateCode" label="GST state code (2 digit)" extra="e.g. 27 = Maharashtra, 29 = Karnataka">
            <Input maxLength={4} placeholder="27" />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
